/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useMemo } from 'react';

import { useRouter } from 'libs/router/useRouter';
import { getAbsoluteRoute } from 'libs/router/helpers';

import { ContentContext } from 'context/ContentContext';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import { FormHubspot } from 'features/customers/organisms/FormHubspot/FormHubspot';
import { ModernFormCss } from 'features/customers/organisms/FormHubspot/ModernFormCss';
import { WhereWeAre } from 'features/customers/organisms/WhereWeAre/WhereWeAre';

import styles from './ContactUsPage.module.scss';

const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className={styles.contactArrow}>
    <path
      d="M2.5 9.5 9.5 2.5M9.5 2.5H4M9.5 2.5V8"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SOCIAL_LINKS = [
  { key: 'twitter', icon: 'media-1', label: 'Global Database on Twitter' },
  { key: 'linkedin', icon: 'media-2', label: 'Global Database on LinkedIn' },
  { key: 'facebook', icon: 'media-4', label: 'Global Database on Facebook' },
] as const;

const formIds = {
  es: 'dd4f9597-767f-43a7-80fb-718b51bbad73',
  it: '3ac656ba-bb2d-491e-82cd-5160bf2422e8',
  fr: 'f7d1a513-9d24-4dc9-9b00-22051668e10f',
  default: '77a47228-7dfe-46bd-bc16-f545358d7ba6',
} as const;

const ContactUs = () => {
  const { query } = useRouter();
  const { secondaryNavigation, data: content, pageVars } = useContext(ContentContext);

  const commonPageVars = useMemo(() => pageVars.common || {}, [pageVars.common]);
  const contactPageVars = useMemo(() => pageVars.contacts || {}, [pageVars.contacts]);

  const redirectUrl = useMemo(
    () => getAbsoluteRoute(secondaryNavigation.thank_you?.slug, { lang: `${query.lang}` }),
    [query.lang, secondaryNavigation.thank_you?.slug],
  );

  return (
    <>
      <div className={styles.hero}>
        <div className={styles.heroDecoration} aria-hidden="true">
          <div className={styles.bgGrid} />
          <div className={styles.bgGlow} />
          <div className={styles.blobA} />
          <div className={styles.blobB} />
        </div>

        <div className={styles.container}>
          <TitleBlock size="page" title={content.title} className={styles.pageTitle} />

          <div className={styles.grid}>
            <div className={styles.info}>
              <span className={styles.infoGlow} aria-hidden="true" />

              <h2 className={styles.infoTitle}>{contactPageVars.left_block_title}</h2>

              <div className={styles.contactList}>
                <a className={styles.contactItem} href={`tel:${commonPageVars.phone}`}>
                  <span className={styles.contactIcon}>
                    <Icon type="phone-1" />
                  </span>
                  <span className={styles.contactBody}>
                    <span className={styles.contactLabel}>Phone</span>
                    <span className={styles.contactValue}>{commonPageVars.phone}</span>
                  </span>
                  <ArrowIcon />
                </a>

                <a className={styles.contactItem} href={`mailto:${commonPageVars.email}`}>
                  <span className={styles.contactIcon}>
                    <Icon type="email" />
                  </span>
                  <span className={styles.contactBody}>
                    <span className={styles.contactLabel}>Email</span>
                    <span className={styles.contactValue}>{commonPageVars.email}</span>
                  </span>
                  <ArrowIcon />
                </a>
              </div>

              <div className={styles.social}>
                <h5 className={styles.socialTitle}>{commonPageVars.social_media_title}</h5>

                <div className={styles.socialRow}>
                  {SOCIAL_LINKS.map(({ key, icon, label }) =>
                    commonPageVars[key] ? (
                      <a
                        key={key}
                        href={commonPageVars[key]}
                        aria-label={label}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={styles.socialLink}
                      >
                        <Icon type={icon} />
                      </a>
                    ) : null,
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formCard}>
              <span className={styles.formGlow} aria-hidden="true" />

              <FormHubspot
                data={formIds}
                redirectUrl={redirectUrl}
                css={ModernFormCss}
                formClassName={styles.hubspotForm}
              />
            </div>
          </div>
        </div>
      </div>

      <WhereWeAre />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default ContactUs;
